import { Injectable } from '@angular/core';
import { PH_SEASON_BREAKDOWN } from '../constants/common-enum';

@Injectable({
    providedIn: 'root',
})
export class CommonFunction {
    constructor() { }

    // convert date to ISO string
    static convertDateToISOString(dateStr: string | Date): any {
        if (!dateStr) return null;

        // If it's already a Date object
        if (dateStr instanceof Date) {
            if (isNaN(dateStr.getTime())) return null;
            const year = dateStr.getFullYear();
            const month = (dateStr.getMonth() + 1).toString().padStart(2, '0');
            const day = dateStr.getDate().toString().padStart(2, '0');
            return new Date(`${year}-${month}-${day}T00:00:00Z`).toISOString();
        }

        // If it's a string in dd/mm/yyyy format
        if (typeof dateStr == 'string' && dateStr?.length >= 10) {
            const [day, month, year] = dateStr.split('/');
            const isoString = new Date(
                `${year}-${month}-${day}T00:00:00Z`
            ).toISOString();
            return isoString;
        }

        return null;
    }

    // convert ISO string to date
    static convertISOStringToDate(isoDate: string): string | null {
        if (!isoDate) return null;

        const date = new Date(isoDate);
        if (isNaN(date.getTime())) return null;

        const day = date.getDate().toString().padStart(2, '0'); // local day
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); // local month
        const year = date.getFullYear(); // local year

        return `${day}/${month}/${year}`;
    }

    static convertISOStringToDateWithOffset(isoDate: string): Date | null {
        if (!isoDate) return null;

        const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!match) return null;

        const [, year, month, day] = match;
        return new Date(+year, +month - 1, +day); // local date, no shift
    }

    static convertDateStringToDate(dateStr: string): Date | null {
        if (!dateStr) return null;

        // Parse DD/MM/YYYY format
        const parts = dateStr.split('/');
        if (parts.length !== 3) return null;

        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

        return new Date(year, month - 1, day);
    }


    static convertTo12Hour(time24h: string): string {
        let [hours, minutes] = time24h.split(':').map(Number);

        const modifier = hours >= 12 ? 'PM' : 'AM';

        hours = hours % 12;
        hours = hours === 0 ? 12 : hours;

        return `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')} ${modifier}`;
    }


    static convertTo24Hour(time12h: string): string {
        const [time, modifier] = time12h.split(' ');
        let [hours, minutes] = time.split(':').map(Number);

        if (modifier === 'PM' && hours !== 12) {
            hours += 12;
        }

        if (modifier === 'AM' && hours === 12) {
            hours = 0;
        }

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    static getInitials(fullName: string): string {
        if (!fullName) return '';
        const names = fullName.split(' ');
        const firstNameInitial = names[0] ? names[0][0] : '';
        const lastNameInitial = names[1] ? names[1][0] : '';
        return (firstNameInitial + lastNameInitial).toUpperCase();
    }

    //Convert Date Month to ISO
    static convertDayMonthToISOString(dateStr: string): string | null {
        if (!dateStr || typeof dateStr !== 'string') return null;

        const parts = dateStr.trim().split(' ');
        if (parts.length !== 2) return null;

        const [dayStr, monthStr] = parts;

        const day = Number(dayStr);
        if (isNaN(day)) return null;

        const monthMap: Record<string, number> = {
            jan: 1, feb: 2, mar: 3, apr: 4,
            may: 5, jun: 6, jul: 7, aug: 8,
            sep: 9, oct: 10, nov: 11, dec: 12
        };

        const month = monthMap[monthStr.toLowerCase()];
        if (!month) return null;

        const currentYear = new Date().getFullYear();

        const isoDate = new Date(
            `${currentYear}-${month.toString().padStart(2, '0')}-${day
                .toString()
                .padStart(2, '0')}T00:00:00Z`
        );

        if (isNaN(isoDate.getTime())) return null;

        return isoDate.toISOString();
    }


    //Convert ISO To Date Month
    static convertISOToDayMonth(isoDate: string): string | null {
        if (!isoDate) return null;

        const date = new Date(isoDate);
        if (isNaN(date.getTime())) return null;

        const day = date.getDate().toString().padStart(2, '0');

        const monthNames = [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];

        const month = monthNames[date.getMonth()];

        return `${day} ${month}`;
    }

    // After testing Bug 780: Unable to select a Quota, use below function before go live

    // static getYears(): any[] {
    //     const CurrentYear = new Date().getFullYear();
    //     return Array.from({ length: 15 }, (_, i) => {
    //         const year = CurrentYear + i;
    //         return { name: year.toString(), value: year };
    //     });
    // }

    // For Ticket Bug 780: Unable to select a Quota; allowed to create past year quota version for PH test && Task 1034: Quota Versions - add options to create 2019 and 2020
    static getYears(): any[] {
        const currentYear = new Date().getFullYear();

        return Array.from({ length: 21 }, (_, i) => {
            const year = currentYear - 7 + i; // 7 years back
            return { name: year.toString(), value: year };
        });
    }

    /**
     * Calculate quota year end date from start date (one day before the anniversary)
     * Example: "24 JAN" -> "23 JAN" (one year later minus one day)
     * Handles leap years correctly
     */
    static calculateQuotaYearEnd(startDate: string): string | null {
        if (!startDate || !startDate.trim()) return null;

        const monthMap: { [key: string]: number } = {
            'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
            'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
        };

        const parts = startDate.trim().split(' ');
        if (parts.length !== 2) return null;

        const day = parseInt(parts[0]);
        const monthAbbr = parts[1].toUpperCase();
        const month = monthMap[monthAbbr];

        if (isNaN(day) || month === undefined) return null;

        const currentYear = new Date().getFullYear();
        const startDateObj = new Date(currentYear, month, day);

        // Add one year
        const endDateObj = new Date(startDateObj);
        endDateObj.setFullYear(endDateObj.getFullYear() + 1);

        // Subtract one day to get the day before the anniversary
        endDateObj.setDate(endDateObj.getDate() - 1);

        const endDay = String(endDateObj.getDate()).padStart(2, '0');
        const endMonthAbbr = Object.keys(monthMap).find(key => monthMap[key] === endDateObj.getMonth());

        return `${endDay} ${endMonthAbbr}`;
    }


    /**
     * Calculate quota year end date from start date in DD/MM/YYYY format
     * Returns date in DD/MM/YYYY format (one day before the anniversary)
     * Example: "24/01/2025" -> "23/01/2026"
     * Handles leap years correctly
     */
    static calculateQuotaYearEndFullDate(startDate: string): string | null {
        if (!startDate) return null;

        const parts = startDate.split('/');
        if (parts.length !== 3) return null;

        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1;
        const year = parseInt(parts[2]);

        if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

        const startDateObj = new Date(year, month, day);

        // Add one year
        const endDateObj = new Date(startDateObj);
        endDateObj.setFullYear(endDateObj.getFullYear() + 1);

        // Subtract one day to get the day before the anniversary
        endDateObj.setDate(endDateObj.getDate() - 1);

        const endDay = String(endDateObj.getDate()).padStart(2, '0');
        const endMonth = String(endDateObj.getMonth() + 1).padStart(2, '0');
        const endYear = endDateObj.getFullYear();

        return `${endDay}/${endMonth}/${endYear}`;
    }

    static getSeasons(): { name: string; value: number }[] {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1; // 1–12

        // Determine current season start year (Oct–Sep season)
        const seasonStartYear = currentMonth >= 10 ? currentYear : currentYear - 1;

        const years: { name: string; value: number }[] = [];

        Array.from({ length: 15 }, (_, i) => {
            const year = seasonStartYear - i;

            years.push({
                name: `${year}/${year + 1}`,
                value: year,
            });
        });

        return years;
    }

    static getComplianceReportSeasons(): { name: string; value: number }[] {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1;
        const firstSeasonYear = 2026;
        const seasonStartYear = Math.max(
            currentMonth >= 10 ? currentYear : currentYear - 1,
            firstSeasonYear
        );

        return Array.from(
            { length: seasonStartYear - firstSeasonYear + 1 },
            (_, i) => {
                const year = seasonStartYear - i;
                return {
                    name: `${year}/${year + 1}`,
                    value: year,
                };
            }
        );
    }

    static getSeasonsPH(): any[] {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 5 }, (_, i) => {
            const year = currentYear - 3 + i;
            return {
                name: `${year}-${year + 1}`,
                value: year,
            };
        });
    }

    //get current season year
    static getCurrentSeasonYear(): number {
        const today = new Date();
        const month = today.getMonth() + 1;
        return month >= 10 ? today.getFullYear() : today.getFullYear() - 1;
    }

    static formatDate(date: Date): string {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    static getYearForFilters(): any[] {
        const CurrentYear = new Date().getFullYear();
        return Array.from({ length: 11 }, (_, i) => {
            const year = CurrentYear - i;
            return { name: year.toString(), value: year };
        });
    }
}